import { useCallback } from 'react';
import { Button, IconGear, Wrapper } from '@screentone/core';

import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ModuleContainer, PageModule } from 'data/generated/graphql';
import { PageModuleModal } from 'features/section-page-edit/components/page-draft-section/components/page-module-modal/PageModuleModal';

interface EditModuleProps {
  hierarchyId: string;
  module: ModuleContainer;
  isAdmin: boolean;
  viewOnly?: boolean;
}

export const EditModule = ({ hierarchyId, viewOnly, module, isAdmin }: EditModuleProps) => {
  const { modifyEntity } = useDataModelContext();
  const { setActionModalComponent, setIsModalOpen } = useContextMenuActions();

  const handleDismissEditModule = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const onPageModuleChange = useCallback(
    (
      newPageModule: PageModule,
      moduleId: string,
      hasCustomModuleId: boolean,
      entitlements: string[],
      ribbonUrl?: string
    ) => {
      modifyEntity(hierarchyId, (entity) => {
        const module = entity as ModuleContainer;
        module.attributes.pageModule = newPageModule;
        module.attributes.moduleId = moduleId;
        module.attributes.hasCustomModuleId = hasCustomModuleId;
        module.attributes.entitlements = entitlements;
        module.attributes.ribbonUrl = ribbonUrl ?? undefined;
        // Also set the moduleId on pageModule for consistency
        module.attributes.pageModule.moduleId = moduleId || undefined;
      });
    },
    [hierarchyId, modifyEntity]
  );

  const handleEditModule = () => {
    setIsModalOpen(true);

    setActionModalComponent(
      <PageModuleModal
        onChange={onPageModuleChange}
        pageModule={module.attributes.pageModule as PageModule}
        onDismiss={handleDismissEditModule}
        viewOnly={viewOnly}
        showIdEdit={isAdmin}
        showEntitlements={isAdmin}
        disableModuleIdEdit={module.attributes.disableModuleIdEdit ?? false}
        moduleId={typeof module.attributes.moduleId === 'string' ? module.attributes.moduleId : ''}
        ribbonUrl={typeof module.attributes.ribbonUrl === 'string' ? module.attributes.ribbonUrl : ''}
        /* eslint-disable @typescript-eslint/no-unsafe-assignment */
        hasCustomModuleId={module.attributes.hasCustomModuleId ?? false}
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        entitlements={(module.attributes.entitlements ?? []) as string[]}
      />
    );
  };

  return (
    <Wrapper>
      <Button tertiary icon={IconGear} onClick={handleEditModule}>
        Module Options
      </Button>
    </Wrapper>
  );
};
